import React, { useState, useEffect, useMemo } from 'react'
import useStore from 'state/knovStore'
import cn from 'classnames'
import styles from 'components/panels/search-header.module.scss'
import FilterIcon_, { UserStream } from 'components/filters/FilterIcon'
import ChannelStats from 'components/shared/ChannelStats'
/** @type {React.ComponentType<any>} */
const FilterIcon = FilterIcon_
import FilterOptions from 'components/filters/FilterOptions'
import FilterOptionsControl_ from 'components/filters/FilterOptionsControl'
/** @type {React.ComponentType<any>} */
const FilterOptionsControl = FilterOptionsControl_
import FilterTabs from 'components/filters/FilterTabs'
import ScopedContentTabs from 'components/filters/ScopedContentTabs'
import MessageRequestsEntry from 'components/filters/MessageRequestsEntry'
import PublicFeedSettingsButton from 'components/filters/PublicFeedSettingsButton'
import StreamHeaderMenu from 'components/panels/StreamHeaderMenu'
import menuStyles from 'components/panels/stream-header-menu.module.scss'
import NotificationTabs from 'components/filters/NotificationTabs'
import SearchBar from 'components/panels/SearchBar'
import StreamOrder from 'components/panels/StreamOrder'
import Tabs from 'components/filters/Tabs'
import ArtistsOrder from 'components/panels/ArtistsOrder'
import DiscoverOrder from 'components/panels/DiscoverOrder'
import {
    musicTabsFor,
    setMusicPanelView,
    useMusicPanelView,
} from 'components/panels/musicPanelView'
import OrdinalsMinSatsPill from 'components/panels/OrdinalsMinSatsPill'
import { ordinalsStreamQueryFilter } from 'components/panels/ordinalsThreshold'
import { getStreamOrderQueryKey } from 'components/panels/streamOrderMapping'
import api from 'api/api'
import { homePrivateEnabled } from 'lib/homePrivate'
import { resolveRefreshPresentation } from 'lib/refreshRedesign'
import useStreamFilter from 'components/filters/useStreamFilters'
import WithTooltip from 'components/shared/WithTooltip'
import ShareTray from 'components/shared/ShareTray'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useSlideToPanel from 'refactor/hooks/useSlideToPanel'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useTeamLookup from 'components/teams/useTeamLookup'
import queryClient from 'api/queryClient'
import { getQuestsQueryKey } from 'refactor/hooks/api/useGetQuests'
import { naturalFeedLayoutEnabled } from 'lib/mediaBuckets'
import { resetNaturalFeedSessionSnapshot } from 'lib/naturalFeedSession'
import useGetUser from 'refactor/hooks/api/useGetUser'
import { Participants, nonUserParticipants, ParticipantsFromIds } from 'components/people/People'
import { getCachedQuest } from 'state/cache'
import { getDepth, pathFromFilter, withDepth } from 'components/filters/useStreamFilters'
import { clearNotificationCount } from 'utils/notificationUtils'
import perms from 'lib/perms'
import { getShowcaseShareCardUrl, getShowcaseShareUrl } from 'utils/shareUtils'
import {
    Waves,
    Plus,
    Share2,
    UserPlus,
    Globe,
    Users,
    LockKeyhole,
    Compass,
    Store,
    Activity,
    UserRound,
    MessageCircle,
    AtSign,
    Mail,
} from 'lucide-react'
import { setPanelViewControls, clearPanelViewControls } from 'lib/panelViewControls'
import { fixedHeaderEnabled } from 'lib/fixedHeader'
import StreamInviteTray from 'components/teams/StreamInviteTray'
import StreamJoinModal from 'components/teams/StreamJoinModal'
import SearchIcon from 'components/shared/icons/SearchIcon'
import { getParticipantIdsFromFilter } from 'lib/filterUtils'
import { clearStream } from 'components/filters/streamFilterUtils'
import { myStreamsEnabled } from 'lib/myStreamsFeature'
import {
    activeScopedContentTab,
    scopedContentTabFilterKeepingOrder,
    scopedContentSurface,
    scopedContentTabs,
} from 'components/filters/contentTabs'
import { isRadioStreamFilter } from 'components/panels/feedNewItemsEligibility'
import RadioHeaderCard from 'components/panels/RadioHeaderCard'
import RadioOnAirHeader from 'components/panels/RadioOnAirHeader'

export default function SearchHeader(props) {
    const [hover, setHover] = useState(false)
    const { panel, hide, isRefreshing, isInitialLoading, refreshSource } = usePanelContext()
    // Initial loading and programmatic reloads (nav/search) light this card's
    // own border instead of dropping pull chrome over it.
    const refreshHalo = resolveRefreshPresentation({
        isInitialLoading: !!isInitialLoading,
        isRefreshing: !!isRefreshing,
        source: refreshSource ?? null,
    }).showProgrammaticFeedback
    const panelId = panel.panelId
    const filter = panel.filter
    const { setStreamFilter, selectPublic, selectFollowing, selectPrivate } = useStreamFilter(
        panelId,
        { append: false },
    )
    const hasMyStreams = myStreamsEnabled()
    const scopedTabSurface = scopedContentSurface(filter)
    const activeScopedTab = activeScopedContentTab(filter)
    const activeTab = filter?.following ? 'following' : filter?.private ? 'private' : 'all'
    const activeNotificationTab =
        props.activeNotificationTab ||
        (filter?.upvalue_notis
            ? 'upvalues'
            : filter?.system_notis
            ? 'system'
            : filter?.message_notis
            ? 'messages'
            : 'all')
    const query = filter?.query

    //console.log('search header filter', props.panel, filter)
    const DEBUG = useStore(state => state.DEBUG)
    const animationState = useStore(state => state.searchHeaderAnimationStates?.[panelId]?.state)
    const openStreamSettingsModal = useStore(
        state => state.actions.modalActions.openStreamSettingsModal,
    )
    const openCreateStreamModal = useStore(
        state => state.actions.modalActions.openCreateStreamModal,
    )
    const userStreams = useStore(state => state.userStreams)

    // Use hook to open new panels to the right
    const { openRight } = useOpenRight(panelId)

    const [showFilterOptions, setShowFilterOptions] = useState(false)
    const [showHeaderShareTray, setShowHeaderShareTray] = useState(false)
    const [showStreamInviteTray, setShowStreamInviteTray] = useState(false)
    const [showStreamJoinModal, setShowStreamJoinModal] = useState(false)

    const isRadioStream = isRadioStreamFilter(filter)
    const musicView = useMusicPanelView(panelId)
    const showOrder =
        !filter?.notifications &&
        !filter?.history &&
        !filter?.starred &&
        !filter?.participants &&
        !filter?.streams &&
        !isRadioStream
    // filter.memes (the meme hub list) is an ordinary ordered feed now — it
    // shows the shared StreamOrder row like every other slice.

    const isMeme = filter?.meme
    const searchBarPlaceholder = isMeme ? 'Search Meme' : 'Search Channel'
    const filterUserId = filter?.user_ordinals || filter?.user
    const { user: filterUser } = useGetUser(filterUserId)
    const minStyles =
        filter?.notifications ||
        filter?.history ||
        filter?.starred ||
        filter?.participants ||
        filter?.streams
            ? styles.min
            : null

    const memeFilterIconStyles = isMeme ? styles.memeFilterIcon : null
    const memeFilterNameStyles = isMeme ? styles.memeFilterName : null
    const memeControlContainerStyles = isMeme ? styles.memeControlContainer : null

    const slideToPanel = useSlideToPanel()

    // Get the current team if this is a team filter
    const hasTeamFilter = filter?.team_ids?.length === 1
    const currentTeamId = hasTeamFilter ? filter.team_ids[0] : null
    const { team: currentTeam } = useTeamLookup(currentTeamId)
    // The channel's owner, shown as "Hosted by" under the name. Members carry it
    // already; other team payloads only have admin_id, so fetch the user then.
    const teamCreatorId =
        currentTeam && !currentTeam.private_stub && currentTeam.admin_id
            ? String(currentTeam.admin_id)
            : undefined
    const teamCreatorMember = (currentTeam?.users || []).find(
        member => String(member.id) === teamCreatorId,
    )
    const { user: fetchedTeamCreator } = useGetUser(teamCreatorMember ? undefined : teamCreatorId, {
        refetchOnMount: false,
    })
    const teamCreator = teamCreatorMember || fetchedTeamCreator
    const headerShareKind = isMeme ? 'meme' : currentTeam ? 'stream' : null
    const headerShareKey = headerShareKind === 'meme' ? filter.meme : currentTeam?.id
    const headerShareName = headerShareKind === 'meme' ? filter.meme : currentTeam?.name
    const headerShareLabel = headerShareName || `this ${headerShareKind || 'page'}`
    const headerShareUrl = getShowcaseShareUrl(headerShareKind, headerShareKey)
    const headerShareCardUrl = getShowcaseShareCardUrl(headerShareKind, headerShareKey)
    const isCurrentTeamAdmin = perms.amTeamAdmin(currentTeam)
    // Growing the stream is the owner's job, so the owner gets a real Invite
    // button in the header instead of digging through Stream settings.
    const canInviteToStream = !!currentTeam && !currentTeam?.private_stub && isCurrentTeamAdmin
    const isCurrentTeamMember = useMemo(() => {
        const currentUserId = gon.currentUser?.id
        if (!currentUserId || !currentTeam || currentTeam?.private_stub) return false
        if (String(currentTeam.admin_id) === String(currentUserId)) return true

        const hasLoadedMembership = currentTeam.users?.some(
            user => String(user.id) === String(currentUserId),
        )
        if (hasLoadedMembership) return true

        const hasUserStreamMembership = (userStreams || []).some(
            team => String(team.id) === String(currentTeam.id),
        )
        if (hasUserStreamMembership) return true

        return (gon.currentUser?.space_teams || []).some(
            team => String(team.id) === String(currentTeam.id),
        )
    }, [currentTeam, userStreams])
    const canOpenStreamSettings =
        !!currentTeam && !currentTeam?.private_stub && (isCurrentTeamAdmin || isCurrentTeamMember)
    const canJoinStream =
        currentTeam?.public === true &&
        currentTeam?.standing_invitations_allowed === true &&
        !isCurrentTeamMember

    const streamPermsLabel = useMemo(() => {
        if (!currentTeam) return null
        if (currentTeam?.private_stub) return null
        const effectiveRule = rule => {
            const normalizedRule = rule || 'anyone'
            return currentTeam?.public !== true && normalizedRule === 'anyone'
                ? 'members'
                : normalizedRule
        }
        const postRule = effectiveRule(currentTeam?.who_can_post_threads)
        const replyRule = effectiveRule(currentTeam?.who_can_reply_threads)

        const formatRule = (rule, action) => {
            if (rule === 'anyone') return `Anyone can ${action}`
            if (rule === 'members') return `Only members can ${action}`
            return `Only admins can ${action}`
        }

        const replyLabel = formatRule(replyRule, 'reply')
        const replyLabelLower = `${replyLabel.charAt(0).toLowerCase()}${replyLabel.slice(1)}`

        return `${formatRule(postRule, 'post')}, ${replyLabelLower}.`
    }, [
        currentTeam?.id,
        currentTeam?.private_stub,
        currentTeam?.who_can_post_threads,
        currentTeam?.who_can_reply_threads,
    ])

    // State for banner loading
    const [hasBanner, setHasBanner] = useState(false)
    const [bannerChecked, setBannerChecked] = useState(false)
    const [bannerLoading, setBannerLoading] = useState(false)
    const [bannerKey, setBannerKey] = useState(Date.now())

    // Refresh banner when team updates or modal closes
    useEffect(() => {
        if (currentTeamId) {
            setBannerKey(Date.now())
            setHasBanner(false) // Reset to check again
            setBannerChecked(false)
        }
    }, [currentTeamId, currentTeam?.updated_at, userStreams])

    // Force refresh banner when modal state changes
    const isModalOpen = useStore(state => state.isModalOpen)
    useEffect(() => {
        if (!isModalOpen && currentTeam) {
            // Modal just closed, refresh banner
            setBannerKey(Date.now())
            setHasBanner(false)
            setBannerChecked(false)
        }
    }, [isModalOpen])

    const refreshStream = async () => {
        console.log('SearchHeader refreshStream called, filter:', filter)
        // For streams discovery, just trigger the refresh state
        // The Streams component will handle the actual refresh
        if (filter?.streams) {
            console.log('SearchHeader - streams filter detected, calling setIsRefreshing(true)')
            props.setIsRefreshing(true)
            return
        }

        // For meme discovery, the Memes component owns the refresh the same way.
        if (filter?.memes) {
            props.setIsRefreshing(true)
            return
        }

        props.setIsRefreshing(true)

        // Invalidate and refetch quests when the panel mounts
        const newFilter = withDepth(filter)

        // clear notification count if we're refreshing the notifications stream
        if (newFilter?.notifications) {
            await clearNotificationCount()
        }

        const queryKey =
            newFilter?.ordinals || newFilter?.user_ordinals
                ? getStreamOrderQueryKey(ordinalsStreamQueryFilter(newFilter))
                : getQuestsQueryKey(newFilter, newFilter?.notifications ? { clear: true } : {})
        if (naturalFeedLayoutEnabled(newFilter)) {
            resetNaturalFeedSessionSnapshot(String(queryKey[1]))
        }
        await queryClient.invalidateQueries({ queryKey, exact: true })
        props.setIsRefreshing(false)
    }

    const onClickStream = ev => {
        ev.stopPropagation()
        ev.preventDefault()

        refreshStream()
    }

    const openTeamSettings = (ev, team) => {
        ev.preventDefault()
        ev.stopPropagation()

        // Close filter options if open
        setShowFilterOptions(false)

        // Open the modal after a slight delay
        setTimeout(() => {
            openStreamSettingsModal(team, panelId, filter)
        }, 100)
    }

    const openSearch = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        // Create a new search filter preserving the current stream context
        const searchFilter = { search: true, query: '' }

        // Preserve the current stream context
        if (filter?.team_ids) {
            searchFilter.team_ids = filter.team_ids
        } else if (filter?.private) {
            searchFilter.private = true
            if (filter.private_all) searchFilter.private_all = true
            if (filter.private_mentions) searchFilter.private_mentions = true
        } else if (filter?.public) {
            searchFilter.public = true
        } else if (filter?.notifications) {
            searchFilter.notifications = true
        } else if (filter?.starred) {
            searchFilter.starred = true
        } else if (filter?.history) {
            searchFilter.history = true
        } else if (filter?.ordinals) {
            searchFilter.ordinals = true
        } else if (filter?.clip) {
            searchFilter.clip = true
        } else if (filter?.public_clip) {
            searchFilter.public_clip = true
        } else if (filter?.link) {
            searchFilter.link = true
        } else if (filter?.user || filter?.user_ordinals) {
            searchFilter.user = filter.user_ordinals || filter.user
        } else {
            // Default to all if no specific context
            searchFilter.all = true
        }

        // Use orchestrator so off-screen insert slides correctly
        openRight(searchFilter)
    }

    const openHeaderShareTray = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        if (headerShareUrl) setShowHeaderShareTray(true)
    }

    const openStreamInviteTray = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        setShowStreamInviteTray(true)
    }

    const openStreamJoinModal = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        setShowStreamJoinModal(true)
    }

    const handleStreamJoined = ({ team: joinedTeam }) => {
        const teamAfterJoin = joinedTeam || currentTeam
        const upsertJoinedTeam = team => {
            if (!team?.id) return
            const currentStreams = useStore.getState().userStreams || []
            useStore.getState().set({
                userStreams: [
                    ...currentStreams.filter(stream => String(stream.id) !== String(team.id)),
                    team,
                ],
            })
        }

        upsertJoinedTeam(teamAfterJoin)
        setShowStreamJoinModal(false)

        api.getTeam(currentTeam.id)
            .then(upsertJoinedTeam)
            .catch(error => console.warn('Failed to refresh joined channel:', error))
    }

    const lastAction = useStore.getState().panels?.lastAction

    // We get the participantQuest from the prior panel via lastAction so we don't have to wait for the quest.
    let participantQuest = props.quest
    if (
        !participantQuest &&
        panel?.panelId === lastAction?.insert?.panelId &&
        lastAction?.questId
    ) {
        participantQuest = getCachedQuest(lastAction.questId)
    }
    const participants = useMemo(() => nonUserParticipants(participantQuest), [participantQuest])

    // Parse participant IDs from filter.participants key when no quest exists
    const participantIdsFromFilter = useMemo(
        () => getParticipantIdsFromFilter(filter),
        [filter?.participants],
    )

    const showStream = !filter?.participants
    const animationClass =
        (animationState === 'fadeIn' && styles.selectedAnimationFadeIn) ||
        (animationState === 'fadeOut' && styles.selectedAnimationFadeOut) ||
        ''
    const showStreamFilterSettingsButton =
        !!gon.currentUser &&
        !!(
            filter?.public ||
            filter?.following ||
            filter?.team_ids?.length ||
            filter?.all ||
            filter?.private ||
            filter?.link ||
            filter?.clip ||
            filter?.public_clip ||
            filter?.ordinals ||
            filter?.treechat ||
            filter?.hodlocker ||
            filter?.twetch ||
            filter?.history ||
            filter?.starred ||
            filter?.notifications
        )
    const showStreamHeaderMenu =
        !!currentTeam && (showStreamFilterSettingsButton || canOpenStreamSettings)
    const isPublicStreamContext =
        filter?.public === true &&
        !filter?.all &&
        !filter?.team_ids?.length &&
        !filter?.treechat &&
        !filter?.hodlocker &&
        !filter?.twetch &&
        !filter?.public_clip &&
        !filter?.clip &&
        !filter?.private &&
        !filter?.link &&
        !filter?.ordinals &&
        !filter?.history &&
        !filter?.starred &&
        !filter?.notifications

    // Scope tabs swap the filter in place. In-place swaps bypass the slide-driven
    // URL sync, so push the URL and persist here.
    const handleTabChange = tab => {
        const nextFilter = clearStream(filter)
        const selectionOptions = filter?.order ? { order: filter.order } : {}
        if (filter?.order) nextFilter.order = filter.order
        if (tab === 'following') {
            selectFollowing(selectionOptions)
            nextFilter.following = true
            api.setDefaultHomeStream('following')
        } else if (tab === 'private') {
            selectPrivate(selectionOptions)
            nextFilter.private = true
            if (hasMyStreams) nextFilter.private_all = true
        } else {
            selectPublic(selectionOptions)
            nextFilter.public = true
            api.setDefaultHomeStream('public')
        }
        history.pushState({ filter: nextFilter }, '', pathFromFilter(nextFilter))
        api.updateUserSpaceOptions({ filter: nextFilter })
    }

    const handleScopedTabChange = tab => {
        if (!scopedTabSurface) return

        const nextFilter = {
            ...clearStream(filter),
            ...scopedContentTabFilterKeepingOrder(scopedTabSurface, tab, filter),
        }
        setStreamFilter(nextFilter)
        history.pushState({ filter: nextFilter }, '', pathFromFilter(nextFilter))
        api.updateUserSpaceOptions({ filter: nextFilter })
    }

    // Into the message requests list from Direct Messages, or back.
    const handleMessageRequestsToggle = showRequests => {
        const nextFilter = {
            ...clearStream(filter),
            private: true,
            order: 'time',
            ...(showRequests ? { private_message_requests: true } : { private_messages: true }),
        }
        setStreamFilter(nextFilter)
        history.pushState({ filter: nextFilter }, '', pathFromFilter(nextFilter))
        api.updateUserSpaceOptions({ filter: nextFilter })
    }

    const handleNotificationTabChange = tab => {
        if (props.onNotificationTabChange) {
            props.onNotificationTabChange(tab)
        }
    }

    const handleArchivedOnlyChange = archivedOnly => {
        const nextFilter = { ...filter }
        if (archivedOnly) {
            nextFilter.archived = true
        } else {
            delete nextFilter.archived
        }

        setStreamFilter(nextFilter)
    }

    // Each standalone content surface owns its local personal tabs.
    const showScopedContentTabs = hasMyStreams && scopedTabSurface !== null
    const directMessagesFeed = !!(filter?.private_messages || filter?.private_message_requests)
    // Following | Public scope tabs appear on Home. Private joins them and leaves
    // the sidebar/navbar under the home_private flag.
    const showFilterTabs =
        filter?.public === true ||
        filter?.following === true ||
        (filter?.private === true && homePrivateEnabled())
    const showNotificationTabs = filter?.notifications === true
    // Ordinal feeds place their order and optional public threshold controls on
    // one wrapping row. The Market tab shares the wall's quality dial; only My
    // Ordinals has no public quality threshold.
    const isOrdinalsStream = filter?.ordinals === true
    const isPublicOrdinalsStream = isOrdinalsStream && filter?.ordinals_scope !== 'mine'
    const showOrdinalsThreshold = isPublicOrdinalsStream

    // fixed_header: publish this list's tabs (and its name over them) so the
    // panel's top bar can offer the same switch once this card scrolls away.
    useEffect(() => {
        if (!fixedHeaderEnabled() || !panelId) return
        if (showFilterTabs) {
            const loggedIn = !!gon.currentUser
            const options = !showOrder
                ? [{ key: 'following', label: 'Following', icon: Users }]
                : [
                      { key: 'all', label: 'Public', icon: Globe },
                      ...(loggedIn ? [{ key: 'following', label: 'Following', icon: Users }] : []),
                      ...(loggedIn && homePrivateEnabled()
                          ? [{ key: 'private', label: 'Private', icon: LockKeyhole }]
                          : []),
                  ]
            setPanelViewControls(panelId, {
                title: 'Home',
                tabs: { options, active: activeTab, select: handleTabChange },
            })
        } else if (showScopedContentTabs) {
            const icons = {
                discover: Compass,
                items: Store,
                activity: Activity,
                mine: UserRound,
                following: Users,
                all: MessageCircle,
                threads: MessageCircle,
                mentions: AtSign,
                messages: Mail,
            }
            setPanelViewControls(panelId, {
                // The DMs nav item's feed (and its requests): its own place, no
                // Private tabs.
                title: directMessagesFeed
                    ? 'Direct Messages'
                    : {
                          streams: 'Channels',
                          memes: 'Memes',
                          ordinals: 'Ordinals',
                          private: 'Private',
                          market: 'Market',
                      }[scopedTabSurface],
                tabs: {
                    options: (directMessagesFeed ? [] : scopedContentTabs(scopedTabSurface)).map(
                        tab => ({
                            key: tab.key,
                            label: tab.label,
                            icon: icons[tab.key],
                        }),
                    ),
                    active: activeScopedTab,
                    select: handleScopedTabChange,
                },
            })
        } else {
            clearPanelViewControls(panelId, 'tabs')
            clearPanelViewControls(panelId, 'title')
        }
    })
    useEffect(
        () => () => {
            if (!panelId) return
            clearPanelViewControls(panelId, 'tabs')
            clearPanelViewControls(panelId, 'title')
        },
        [panelId],
    )

    return (
        <div
            data-testid="search-header"
            className={cn(
                styles.searchHeaderComp,
                `search-header-comp ${props.panel}`,
                minStyles,
                animationClass,
                hasTeamFilter && styles.withBanner,
                // Tabbed surfaces demote the scope title to an eyebrow so the tabs
                // read as the header (option A).
                (showFilterTabs || showScopedContentTabs) && styles.tabbedSurface,
                refreshHalo && styles.refreshHalo,
            )}
            style={{ visibility: hide || props.hideDuringScroll ? 'hidden' : 'visible' }}
            onClick={ev => slideToPanel()}
        >
            {hasTeamFilter && (
                <div className={styles.teamBannerContainer}>
                    {currentTeamId && hasBanner && (
                        <img
                            key={`banner-${bannerKey}`}
                            src={`${window.knovApiUrl}/api/v1/teams/${currentTeamId}/banner?size=medium&t=${bannerKey}&break_cache=true`}
                            onError={e => {
                                setBannerLoading(false)
                                setHasBanner(false)
                            }}
                            onLoad={e => {
                                setBannerLoading(false)
                                setHasBanner(true)
                            }}
                            className={styles.teamBanner}
                            alt=""
                        />
                    )}
                    {currentTeamId && !hasBanner && bannerChecked && (
                        <div className={styles.bannerPlaceholder}>
                            <Waves className={styles.wavesIcon} size={40} />
                        </div>
                    )}
                    {currentTeamId && !bannerChecked && (
                        <div className={styles.teamBannerSkeleton} />
                    )}
                </div>
            )}
            {currentTeamId && !bannerChecked && (
                <img
                    key={`banner-check-${bannerKey}`}
                    src={`${window.knovApiUrl}/api/v1/teams/${currentTeamId}/banner?size=medium&t=${bannerKey}&break_cache=true`}
                    onError={() => {
                        setHasBanner(false)
                        setBannerChecked(true)
                    }}
                    onLoad={() => {
                        setHasBanner(true)
                        setBannerChecked(true)
                    }}
                    style={{ display: 'none' }}
                    alt=""
                />
            )}

            {DEBUG && (
                <div className={styles.debugContainer}>
                    <span className={styles.debugText}>
                        Filter:{' '}
                        <span
                            style={{ fontFamily: 'monospace', wordWrap: 'break-word' }}
                            onClick={() => {
                                const text = JSON.stringify(filter)
                                navigator.clipboard.writeText(text).catch(err => {
                                    console.error('Failed to copy filter to clipboard:', err)
                                })
                                // briefly bold the text
                                const spanElement = document.getElementById('filterText')
                                spanElement.style.fontWeight = 'bold'
                                setTimeout(() => {
                                    spanElement.style.fontWeight = 'normal'
                                }, 500)
                            }}
                            id="filterText"
                        >
                            {JSON.stringify(filter)}
                        </span>
                    </span>
                </div>
            )}
            {/* Stream headers mirror the profile header: the logo overlaps the
                banner like the profile avatar, the freed top row carries the
                controls, and the Invite pill sits in the Edit-profile slot. */}
            {showStream && hasTeamFilter && (
                <div className={styles.teamProfileHeader}>
                    <div className={styles.teamAvatarRow}>
                        <div className={styles.teamAvatarWrap}>
                            <div
                                className={styles.filterIconContainer}
                                onClick={onClickStream}
                                onMouseEnter={() => setHover(true)}
                                onMouseLeave={() => setHover(false)}
                            >
                                <FilterIcon
                                    disable={true}
                                    filter={filter}
                                    team={currentTeam}
                                    panel={props.panel}
                                    showNumNoti
                                    contextStyles={styles.teamHeaderLogo}
                                    fullSize
                                    initialStyles={styles.teamHeaderLogoInitials}
                                />
                            </div>
                        </div>

                        <div
                            className={cn(
                                styles.teamControlsContainer,
                                styles.teamAvatarRowActions,
                            )}
                            onClick={ev => ev.stopPropagation()}
                        >
                            <WithTooltip tip="Search in Channel">
                                <div className={styles.teamSearchIcon} onClick={openSearch}>
                                    <SearchIcon size={22} className={styles.teamSearchIconGlyph} />
                                </div>
                            </WithTooltip>

                            {headerShareKind && headerShareUrl && (
                                <WithTooltip tip={`Share ${headerShareKind}`}>
                                    <button
                                        type="button"
                                        className={styles.teamShareIcon}
                                        onClick={openHeaderShareTray}
                                        aria-label={`Share ${headerShareKind}`}
                                        data-testid={`${headerShareKind}-header-share-button`}
                                    >
                                        <Share2 size={22} className={styles.teamShareIconGlyph} />
                                    </button>
                                </WithTooltip>
                            )}

                            {showStreamHeaderMenu && (
                                <StreamHeaderMenu triggerClassName={styles.teamSettingsIcon}>
                                    {close => (
                                        <>
                                            {showStreamFilterSettingsButton && (
                                                <PublicFeedSettingsButton
                                                    className={menuStyles.menuItem}
                                                    label="Channel filters"
                                                    showHideAuctionPosts={isPublicStreamContext}
                                                    archivedOnly={filter?.archived === true}
                                                    onArchivedOnlyChange={handleArchivedOnlyChange}
                                                />
                                            )}

                                            {canOpenStreamSettings && (
                                                <button
                                                    type="button"
                                                    className={menuStyles.menuItem}
                                                    onClick={ev => {
                                                        openTeamSettings(ev, currentTeam)
                                                        close()
                                                    }}
                                                >
                                                    <i className="fa fa-gear" />
                                                    {isCurrentTeamAdmin
                                                        ? 'Channel settings'
                                                        : 'Channel membership'}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </StreamHeaderMenu>
                            )}

                            {canJoinStream && (
                                <WithTooltip tip="Join to see this channel's threads in your Following">
                                    <button
                                        type="button"
                                        className={styles.teamInviteButton}
                                        onClick={openStreamJoinModal}
                                        aria-label="Join this channel"
                                        data-testid="stream-header-join-button"
                                    >
                                        <UserPlus
                                            size={16}
                                            className={styles.teamInviteButtonGlyph}
                                        />
                                        <span>Join</span>
                                    </button>
                                </WithTooltip>
                            )}

                            {canInviteToStream && (
                                <WithTooltip tip="Invite people to this channel">
                                    <button
                                        type="button"
                                        className={styles.teamInviteButton}
                                        onClick={openStreamInviteTray}
                                        aria-label="Invite people to this channel"
                                        data-testid="stream-header-invite-button"
                                    >
                                        <UserPlus
                                            size={16}
                                            className={styles.teamInviteButtonGlyph}
                                        />
                                        <span>Invite</span>
                                    </button>
                                </WithTooltip>
                            )}
                        </div>
                    </div>

                    <div className={styles.teamNameRow}>
                        <FilterOptionsControl
                            filter={filter}
                            team={currentTeam}
                            panel={props.panel}
                            user={gon.currentUser}
                            filterUser={filterUser}
                            showFilterOptions={showFilterOptions}
                            setShowFilterOptions={setShowFilterOptions}
                            contextStyles={styles.teamHeaderName}
                            showName
                            disable
                        />
                    </div>

                    {/* The Music channel is the station: it says it's on air in
                        place of who hosts it, its visibility and its members. */}
                    {isRadioStream && <RadioOnAirHeader />}

                    {!isRadioStream && teamCreator?.id && teamCreator?.name && (
                        <div className={styles.streamCreatedBy} data-testid="stream-created-by">
                            <span>Hosted by</span>
                            <UserStream
                                user={teamCreator}
                                size="small"
                                contextStyles={styles.streamCreatedByAvatar}
                                tip={`View @${teamCreator.name}'s stream`}
                            />
                            <span className={styles.streamCreatedByName}>@{teamCreator.name}</span>
                        </div>
                    )}

                    {!isRadioStream && currentTeam && (
                        <div className={styles.streamMetaRow}>
                            <div className={styles.visibilityPill}>
                                <i
                                    className={`fa ${currentTeam?.public ? 'fa-globe' : 'fa-lock'}`}
                                />
                                {currentTeam?.public ? 'Public' : 'Private'}
                            </div>
                            {streamPermsLabel && (
                                <div className={styles.streamPermsInline}>
                                    {streamPermsLabel}
                                    {currentTeam?.thread_display_mode === 'roots_only'
                                        ? ' Showing root threads only.'
                                        : currentTeam?.thread_display_mode === 'all_branches'
                                        ? ' Showing all branches.'
                                        : ''}
                                </div>
                            )}
                        </div>
                    )}

                    {!isRadioStream && currentTeam && !currentTeam.private_stub && (
                        <ChannelStats team={currentTeam} className={styles.streamStats} />
                    )}

                    {currentTeam?.description && (
                        <div className={styles.streamDescription}>{currentTeam.description}</div>
                    )}
                </div>
            )}

            {showStream && !hasTeamFilter && (
                <div
                    className={cn(
                        styles.streamControlContainer,
                        memeControlContainerStyles,
                        styles.noBanner,
                    )}
                >
                    <div className={styles.filterContainer}>
                        <div
                            className={styles.filterIconContainer}
                            onClick={onClickStream}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                        >
                            <FilterIcon
                                disable={true}
                                filter={filter}
                                team={currentTeam}
                                panel={props.panel}
                                user={filterUser}
                                showNumNoti
                                useCircleOrdinalsIcon
                                contextStyles={cn(styles.searchHeaderIcon, memeFilterIconStyles)}
                                initialStyles={styles.initialStyles}
                                publicIcon={styles.publicIcon}
                                treechatIcon={styles.treechatIcon}
                                hodlockerIcon={styles.hodlockerIcon}
                                twetchIcon={styles.twetchIcon}
                                notiIcon={styles.notiIcon}
                                fontIcon={styles.fontIcon}
                                privateIcon={styles.privateIcon}
                                linksIcon={styles.linksIcon}
                                historyIcon={styles.historyIcon}
                                peopleIcon={styles.peopleIcon}
                            />
                        </div>

                        <div
                            className={styles.filterOptionsControlContainer}
                            //onClick={ev => ev.stopPropagation()}
                        >
                            <FilterOptionsControl
                                filter={filter}
                                team={currentTeam}
                                panel={props.panel}
                                user={gon.currentUser}
                                filterUser={filterUser}
                                showFilterOptions={showFilterOptions}
                                setShowFilterOptions={setShowFilterOptions}
                                nameStyles={cn(styles.name, memeFilterNameStyles)}
                                contextStyles={showFilterTabs ? styles.eyebrowName : undefined}
                                showName
                                disable
                            />
                        </div>
                    </div>

                    <div className={styles.teamControlsContainer}>
                        {!filter?.notifications &&
                            !filter?.starred &&
                            !filter?.history &&
                            !filter?.streams &&
                            !filter?.memes && (
                                <WithTooltip tip="Search in Channel">
                                    <div className={styles.teamSearchIcon} onClick={openSearch}>
                                        <SearchIcon
                                            size={22}
                                            className={styles.teamSearchIconGlyph}
                                        />
                                    </div>
                                </WithTooltip>
                            )}

                        {headerShareKind && headerShareUrl && (
                            <WithTooltip tip={`Share ${headerShareKind}`}>
                                <button
                                    type="button"
                                    className={styles.teamShareIcon}
                                    onClick={openHeaderShareTray}
                                    aria-label={`Share ${headerShareKind}`}
                                    data-testid={`${headerShareKind}-header-share-button`}
                                >
                                    <Share2 size={22} className={styles.teamShareIconGlyph} />
                                </button>
                            </WithTooltip>
                        )}

                        {filter?.streams && gon?.currentUser && (
                            <button
                                type="button"
                                className={styles.teamNewChannelButton}
                                onClick={ev => {
                                    ev.stopPropagation()
                                    openCreateStreamModal(panelId, filter)
                                }}
                                aria-label="New Channel"
                                data-testid="streams-header-new-channel-button"
                            >
                                <Plus size={16} className={styles.teamNewChannelButtonGlyph} />
                                <span>New</span>
                            </button>
                        )}

                        {showStreamFilterSettingsButton && (
                            <WithTooltip tip="Channel filters">
                                <PublicFeedSettingsButton
                                    className={styles.teamFilterSettingsIcon}
                                    iconSize={22}
                                    showHideAuctionPosts={isPublicStreamContext}
                                    archivedOnly={filter?.archived === true}
                                    onArchivedOnlyChange={handleArchivedOnlyChange}
                                />
                            </WithTooltip>
                        )}
                    </div>
                </div>
            )}

            {showFilterTabs && (
                <FilterTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    showOnlyFollowing={!showOrder}
                    isLoggedIn={!!gon.currentUser}
                />
            )}
            {isRadioStream && !filter?.search && <RadioHeaderCard />}
            {isRadioStream && (
                <div className={styles.musicTabs} onClick={ev => ev.stopPropagation()}>
                    <Tabs
                        ariaLabel="Music tabs"
                        activeTab={musicView.tab}
                        onTabChange={tab => setMusicPanelView(panelId, { tab })}
                        tabs={musicTabsFor(!!gon.currentUser)}
                    />
                </div>
            )}
            {showScopedContentTabs && !directMessagesFeed && (
                <ScopedContentTabs
                    surface={scopedTabSurface}
                    activeTab={activeScopedTab}
                    onTabChange={handleScopedTabChange}
                />
            )}
            {showScopedContentTabs &&
                scopedTabSurface === 'private' &&
                (filter?.private_messages || filter?.private_message_requests) && (
                    <MessageRequestsEntry
                        viewingRequests={!!filter?.private_message_requests}
                        onToggle={handleMessageRequestsToggle}
                    />
                )}
            {showNotificationTabs && (
                <NotificationTabs
                    activeTab={activeNotificationTab}
                    onTabChange={handleNotificationTabChange}
                />
            )}

            {showFilterOptions && (
                <div className={styles.filterOptionsContainer}>
                    <FilterOptions
                        origin="search-header"
                        filter={filter}
                        panelId={props.panelId}
                        panel={props.panel}
                        query={props.query}
                        close={() => setShowFilterOptions(!showFilterOptions)}
                        show={['public', 'private', 'user', 'teams']}
                        contextStyles={styles.filterOptions}
                        setShowFilterOptions={setShowFilterOptions}
                        showNoti
                        blur
                    />
                </div>
            )}

            {filter?.meme && (
                <div className={styles.memeHeaderContainer}>
                    <div className={cn(styles.memeIcon, styles.leftMemeIcon)}>
                        <span
                            className={cn(
                                styles.bracketIcon,
                                styles.outerBracket,
                                styles.outerLeftBracket,
                            )}
                        >
                            [
                        </span>
                        <span
                            className={cn(
                                styles.bracketIcon,
                                styles.innerBracket,
                                styles.innerLeftBracket,
                            )}
                        >
                            [
                        </span>
                    </div>

                    <div className={styles.memeHeader}>{filter?.meme}</div>

                    <div className={cn(styles.memeIcon, styles.rightMemeIcon)}>
                        <span
                            className={cn(
                                styles.bracketIcon,
                                styles.innerBracket,
                                styles.innerRightBracket,
                            )}
                        >
                            ]
                        </span>
                        <span
                            className={cn(
                                styles.bracketIcon,
                                styles.outerBracket,
                                styles.outerRightBracket,
                            )}
                        >
                            ]
                        </span>
                    </div>
                </div>
            )}

            {showOrder && (
                <div
                    className={cn(
                        styles.streamOrderContainer,
                        isPublicOrdinalsStream && styles.ordinalsOrderRow,
                    )}
                    onClick={ev => ev.stopPropagation()}
                >
                    <StreamOrder
                        filter={filter}
                        panelId={panelId}
                        panel={props.panel}
                        query={query}
                    />
                    {showOrdinalsThreshold && (
                        <OrdinalsMinSatsPill filter={filter} panelId={panelId} />
                    )}
                </div>
            )}

            {isRadioStream && musicView.tab === 'discover' && (
                <div className={styles.streamOrderContainer} onClick={ev => ev.stopPropagation()}>
                    <DiscoverOrder panelId={panelId} />
                </div>
            )}

            {isRadioStream && musicView.tab === 'artists' && (
                <div className={styles.streamOrderContainer} onClick={ev => ev.stopPropagation()}>
                    <ArtistsOrder panelId={panelId} />
                </div>
            )}

            {filter?.participants && (
                <div className={styles.participantsContainer}>
                    {participants && participants.length > 0 ? (
                        <Participants
                            participants={participants}
                            quest={participantQuest}
                            contextStyles={styles.participants}
                        />
                    ) : participantIdsFromFilter.length > 0 ? (
                        <ParticipantsFromIds
                            participantIds={participantIdsFromFilter}
                            contextStyles={styles.participants}
                        />
                    ) : null}
                </div>
            )}

            <ShareTray
                canShareImageCard={
                    !!headerShareCardUrl &&
                    (headerShareKind === 'meme' || currentTeam?.public === true)
                }
                closeButtonTestId={`${headerShareKind || 'header'}-share-tray-close`}
                dialogTestId={`${headerShareKind || 'header'}-share-tray`}
                isOpen={showHeaderShareTray}
                onDismiss={() => setShowHeaderShareTray(false)}
                shareCardId={headerShareKey}
                shareCardUrl={headerShareCardUrl}
                shareKind={headerShareKind}
                shareText={
                    headerShareKind === 'meme'
                        ? `Explore ${headerShareLabel} on Treechat`
                        : `Check out ${headerShareLabel} on Treechat`
                }
                shareTitle={headerShareName ? `${headerShareName} on Treechat` : 'Treechat'}
                shareUrl={headerShareUrl}
            />

            {canInviteToStream && (
                <StreamInviteTray
                    isOpen={showStreamInviteTray}
                    team={currentTeam}
                    onDismiss={() => setShowStreamInviteTray(false)}
                    onManageStream={ev => {
                        setShowStreamInviteTray(false)
                        openTeamSettings(ev, currentTeam)
                    }}
                />
            )}

            {canJoinStream && (
                <StreamJoinModal
                    isOpen={showStreamJoinModal}
                    team={currentTeam}
                    onDismiss={() => setShowStreamJoinModal(false)}
                    onJoined={handleStreamJoined}
                />
            )}
        </div>
    )
}
